/**
 * Reading an image without a server.
 *
 * Used only by the signed-out demo, where there is nowhere to upload to. Images
 * become data URIs and travel inside the document, which is why the size cap
 * matters: the board schema limits how long an item's `src` may be, and going
 * over it would make the board fail validation the moment it was carried into a
 * real account.
 */

/** Comfortably inside the schema's 500,000-character `src` limit. */
const MAX_DATA_URI_CHARS = 400_000;

export function readImageAsDataUri(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) return Promise.reject(new Error('not an image'));

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('could not read the file'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      if (result.length > MAX_DATA_URI_CHARS) {
        reject(new Error('too large to inline'));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(file);
  });
}

/** Intrinsic size of an image, so it can be placed at its true aspect ratio. */
export function measureImage(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ w: image.naturalWidth || 320, h: image.naturalHeight || 240 });
    image.onerror = () => reject(new Error('could not decode the image'));
    image.src = src;
  });
}
